/** Entry point of the phase-D family package: registers objects, talk, drawers and day hooks. Imported once from main.ts. */
import { apply, celebrate, give, take, toast } from '../core/effects';
import { bus } from '../core/bus';
import { hasGame, S } from '../core/game';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { say } from '../ui/dialog';
import { bumpUnread } from '../phone/phone';
import { openMomThread } from '../phone/chats';
import { dayNum, ensureToday, resetMomLive } from '../phone/momTexts';
import { completeObjective } from './days';
import { gardenDayStart, gardenRainCheck, usePlot, useTap } from './garden';
import { logJournal } from './journal';
import { registerItemUse, registerMenuItem, registerObjects } from './registry';
import '../render/propsFamily';

const min = (n: number) => m().common.minutes(n);

/**
 * The home oven, extended: the built-in cookies and strawberry cake (reproduced exactly,
 * Sunday goals depend on them) plus the family-size chiffon cake made with 3 🍓.
 */
function oven(): void {
  const t = m().oven;
  const f = m().family;
  say({
    who: t.title,
    icon: '🍰',
    text: t.text,
    choices: [
      {
        text: t.cookies,
        cost: min(20),
        run: () => {
          sfx('brew');
          apply({ energy: -3, minutes: 20 }, { float: false });
          toast(m().oven.cookiesWork);
          window.setTimeout(() => {
            give('cookies', 3);
            apply({ mood: 8 });
            celebrate();
            toast(m().oven.cookiesDone);
            completeObjective('cookies');
          }, 900);
        },
      },
      {
        text: `${t.cake} (🍓)`,
        cost: min(40),
        run: () => {
          if (!take('strawberries')) {
            sfx('bad');
            return toast(m().oven.cakeNeeds);
          }
          sfx('brew');
          apply({ energy: -5, minutes: 40 }, { float: false });
          toast(m().oven.cakeWork);
          window.setTimeout(() => {
            give('cake', 1);
            apply({ mood: 12 });
            celebrate(true);
            toast(m().oven.cakeDone);
            completeObjective('cake');
          }, 1100);
        },
      },
      {
        text: `${f.chiffon} (🍓×3)`,
        cost: min(50),
        run: () => {
          if (!take('strawberries', 3)) {
            sfx('bad');
            return toast(m().family.chiffonNeeds);
          }
          sfx('brew');
          apply({ energy: -6, minutes: 50 }, { float: false });
          toast(m().family.chiffonWork);
          window.setTimeout(() => {
            give('chiffon', 1);
            apply({ mood: 15, stars: 5 });
            celebrate(true);
            toast(m().family.chiffonDone);
            logJournal('🍰', 'family.journal.chiffon');
          }, 1300);
        },
      },
    ],
  });
}

registerObjects({
  ...Object.fromEntries(Array.from({ length: 9 }, (_, i) => [`fam_plot${i}`, () => usePlot(i)])),
  fam_tap: useTap,
  oven,
});

// The bag already took the item when these run.
registerItemUse('tomato', () => {
  apply({ energy: 20 });
  sfx('pickup');
  toast(m().family.tomatoEat);
});
registerItemUse('sunflower', () => {
  apply({ mood: 100 - S().stats.mood });
  celebrate();
  toast(m().family.sunflowerLook);
});
registerItemUse('chiffon', () => {
  // At home it's shared with the family; anywhere else it's a big treat for you.
  if (S().player.map === 'home' || S().player.map === 'balcony') {
    apply({ mood: 15, love: { mom: 8, dad: 6, sister: 8 } });
    celebrate(true);
    toast(m().family.chiffonShare);
  } else {
    apply({ mood: 35, energy: 10 });
    celebrate();
    toast(m().family.chiffonEat);
  }
});

registerMenuItem(() => m().family.menuMom, openMomThread);

bus.on('dayStart', () => {
  resetMomLive();
  // Let the weather package settle today's weather first.
  window.setTimeout(() => {
    if (!hasGame()) return;
    gardenDayStart();
    const isNew = !S().momTexts.some((e) => e.day === dayNum());
    ensureToday();
    if (isNew) {
      window.setTimeout(() => {
        bumpUnread();
        sfx('pop');
        toast(m().family.toastNew);
      }, 1800);
    }
  }, 0);
});

// Rain waters the garden: credited once on the first change after it rained.
bus.on('change', gardenRainCheck);

export {};
